import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftLight, PiCaretRightLight } from 'react-icons/pi';
import type { ContractRow, ObligationFeedItem } from '../../utils/contractsApi';

/**
 * Renewals calendar - a month grid of contract expiry dates + obligation due
 * dates (CLM Phase 4).
 *
 * Self-contained (no calendar library): it buckets each contract by its expiry
 * day and each obligation by its due day, and renders a month grid with a marker
 * per item - expiries colour-coded by status (red/amber/neutral), obligations in
 * a distinct accent. Clicking a marker opens the relevant contract. This is the
 * "when is everything happening" view that complements the table.
 */

interface CalItem {
  key: string;            // contract uuid (for navigation)
  label: string;
  kind: 'expiry' | 'obligation';
  tone: 'danger' | 'warning' | 'muted' | 'accent';
  href: string;           // route to open
}

interface Props {
  contracts: ContractRow[];
  obligations?: ObligationFeedItem[];
  onOpen: (uuid: string) => void;
  onOpenHref?: (href: string) => void;
}

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const RenewalsCalendar: React.FC<Props> = ({ contracts, obligations = [], onOpen, onOpenHref }) => {
  const { t } = useTranslation();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  // Map 'YYYY-MM-DD' → calendar items (contract expiries + obligation due dates).
  const byDay = useMemo(() => {
    const m = new Map<string, CalItem[]>();
    const push = (key: string, item: CalItem) => {
      const list = m.get(key) || m.set(key, []).get(key)!;
      list.push(item);
    };
    contracts.forEach((c) => {
      if (!c.expiry_date) return;
      const d = new Date(c.expiry_date);
      if (Number.isNaN(d.getTime())) return;
      push(ymd(d), {
        key: c.uuid, label: c.title, kind: 'expiry',
        tone: c.status === 'expired' ? 'danger' : c.status === 'expiring_soon' ? 'warning' : 'muted',
        href: `/contracts/${c.uuid}`,
      });
    });
    obligations.forEach((o) => {
      if (!o.due_date) return;
      const d = new Date(o.due_date);
      if (Number.isNaN(d.getTime())) return;
      push(ymd(d), {
        key: o.contract_uuid, label: o.description, kind: 'obligation', tone: 'accent',
        href: `/contracts/${o.contract_uuid}?tab=Obligations`,
      });
    });
    return m;
  }, [contracts, obligations]);

  const weeks = useMemo(() => {
    const first = startOfMonth(cursor);
    // Grid starts on the Monday on/before the 1st.
    const offset = (first.getDay() + 6) % 7;
    const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i += 1) {
      cells.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
    }
    const rows: Date[][] = [];
    for (let w = 0; w < 6; w += 1) rows.push(cells.slice(w * 7, w * 7 + 7));
    return rows;
  }, [cursor]);

  const todayKey = ymd(new Date());
  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const dowLabels = useMemo(() => {
    // Mon..Sun, localised short names.
    const base = new Date(2024, 0, 1); // a Monday
    return Array.from({ length: 7 }, (_, i) =>
      new Date(base.getFullYear(), base.getMonth(), base.getDate() + i)
        .toLocaleDateString(undefined, { weekday: 'short' }));
  }, []);

  const toneDot: Record<CalItem['tone'], string> = {
    danger: 'bg-danger-500',
    warning: 'bg-warning-500',
    muted: 'bg-fg-muted/50',
    accent: 'bg-info-500',
  };
  const openItem = (item: CalItem) => (onOpenHref ? onOpenHref(item.href) : onOpen(item.key));

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm text-fg-strong">{monthLabel}</h3>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setCursor((c) => addMonths(c, -1))}
            className="rounded-md border border-hairline p-1 text-fg-muted hover:bg-surface-sunken/60" aria-label={t('Previous month')}>
            <PiCaretLeftLight size={15} />
          </button>
          <button type="button" onClick={() => setCursor(startOfMonth(new Date()))}
            className="rounded-md border border-hairline px-2 py-1 text-[11px] text-fg-muted hover:bg-surface-sunken/60">
            {t('Today')}
          </button>
          <button type="button" onClick={() => setCursor((c) => addMonths(c, 1))}
            className="rounded-md border border-hairline p-1 text-fg-muted hover:bg-surface-sunken/60" aria-label={t('Next month')}>
            <PiCaretRightLight size={15} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px rounded-lg border border-hairline-soft bg-hairline-soft overflow-hidden">
        {dowLabels.map((d) => (
          <div key={d} className="bg-surface px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-fg-muted">{d}</div>
        ))}
        {weeks.flat().map((day) => {
          const key = ymd(day);
          const inMonth = day.getMonth() === cursor.getMonth();
          const items = byDay.get(key) || [];
          const isToday = key === todayKey;
          return (
            <div key={key} className={`min-h-[76px] bg-surface p-1.5 ${inMonth ? '' : 'opacity-40'}`}>
              <div className={`mb-1 text-[11px] ${isToday ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-champagne/20 text-fg-strong' : 'text-fg-muted'}`}>
                {day.getDate()}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((item, idx) => (
                  <button key={idx} type="button" onClick={() => openItem(item)}
                    title={`${item.kind === 'obligation' ? t('Obligation') : t('Renewal')}: ${item.label}`}
                    className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-start text-[10px] text-fg-body hover:bg-surface-sunken/70">
                    <span className={`h-1.5 w-1.5 shrink-0 ${item.kind === 'obligation' ? 'rounded-[2px]' : 'rounded-full'} ${toneDot[item.tone]}`} />
                    <span className="truncate">{item.label}</span>
                  </button>
                ))}
                {items.length > 3 && <div className="px-1 text-[10px] text-fg-muted">+{items.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-fg-muted">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-danger-500" />{t('Expired')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning-500" />{t('Expiring soon')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-fg-muted/50" />{t('Active')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-[2px] bg-info-500" />{t('Obligation due')}</span>
      </div>
    </div>
  );
};

export default RenewalsCalendar;
